import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import firebase from '../services/firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {Loader} from '../components/Loader';
import { Underline } from '../components/Underline';
import { SignIn } from '../pages/Login';

export const Profile = () => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [name, setName] = useState(user.name);
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const userOrders = await firebase.getOrdersByUserId(user.uid);
      setOrders(userOrders);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Error fetching orders: ' + error.message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      console.error('Error fetching orders:', error);
    }
  };

  const updateProfile = async () => {
    setLoading(true);
    try {
      await firebase.updateProfile(user.uid, { name, phoneNumber });
      toast.success('Profile updated successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Error updating profile: ' + error.message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      console.error('Error updating profile:', error);
    }
  };

  const changePassword = async () => {
    setLoading(true);
    try {
      await firebase.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      setCurrentPassword('');
      setNewPassword('');
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Error changing password: ' + error.message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      console.error('Error changing password:', error);
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-[20px] flex-wrap w-full">
      {user?.isAuthenticated ? <>
        <div className="w-full flex-1 bg-white p-[20px] shadow-md">
          <h2 className="text-xl font-semibold mb-4">Profile Details</h2>
          <Underline />
          {loading ? (
            <Loader />
          ) : (
            <div>
              <label className="block font-medium mb-2">Name:</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-primary"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <label className="block font-medium mt-4 mb-2">Phone Number:</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-primary"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              <button
                className="bg-primary text-white font-medium py-2 px-4 mt-4 hover:bg-primaryDark"
                onClick={updateProfile}
              >
                Update Profile
              </button>
            </div>
          )}
        </div>

        <div className="w-full flex-1 bg-white p-[20px] shadow-md">
          <h2 className="text-xl font-semibold mb-4">Change Password</h2>
          <Underline />
          {loading ? (
            <Loader />
          ) : (
            <div>
              <label className="block font-medium mb-2">Current Password:</label>
              <input
                type="password"
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-primary"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <label className="block font-medium mt-4 mb-2">New Password:</label>
              <input
                type="password"
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-primary"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                className="bg-primary text-white font-medium py-2 px-4 mt-4 hover:bg-primaryDark"
                onClick={changePassword}
              >
                Change Password
              </button>
            </div>
          )}
        </div>

        <div className="w-full bg-white p-[20px] shadow-md">
          <h2 className="text-xl font-semibold mb-4">Order History</h2>
          <Underline />
          {loading ? (
            <Loader />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px]">
              <div className='flex flex-col flex-1 gap-[20px]'>
                {orders.map((order, index) => (
                  <>
                    {index%2 === 1 && <div
                      key={order.orderId}
                      className="bg-gray-100 p-[20px] shadow-sm"
                    >
                      <h3 className="text-lg font-bold mb-2">Order ID: {order.orderId}</h3>
                      <p className="text-sm text-gray-500 mb-2">
                        Status: {order.status || 'Pending'} &nbsp;&nbsp;&nbsp; | &nbsp;&nbsp;&nbsp; Total: ₹{order.total}
                      </p>
                      <div className='flex flex-col md:flex-row flex-wrap gap-[10px] mt-5'>
                        {order.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex flex-row items-center justify-between w-[calc(50%-10px)] p-3 bg-white"
                          >
                            <p className="text-md">{item.product.name}</p>
                            <p className="text-sm bg-gray-100 py-1 rounded-full px-5">
                              {item.quantity} x {item.size}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>}
                  </>
                ))}
              </div>
              
              <div className='flex flex-col flex-1 gap-[20px]'>
                {orders.map((order, index) => (
                  <>
                    {index%2 === 0 && <div
                      key={order.orderId}
                      className="bg-gray-100 p-[20px] shadow-sm"
                    >
                      <h3 className="text-lg font-bold mb-2">Order ID: {order.orderId}</h3>
                      <p className="text-sm text-gray-500 mb-2">
                        Status: {order.status || 'Pending'} &nbsp;&nbsp;&nbsp; | &nbsp;&nbsp;&nbsp; Total: ₹{order.total}
                      </p>
                      <div className='flex flex-col md:flex-row flex-wrap gap-[10px] mt-5'>
                        {order.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex flex-row items-center justify-between w-[calc(50%-10px)] p-3 bg-white"
                          >
                            <p className="text-md">{item.product.name}</p>
                            <p className="text-sm bg-gray-100 py-1 rounded-full px-5">
                              {item.quantity} x {item.size}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>}
                  </>
                ))}
              </div>

              {orders.length === 0 && (
                <div className="text-center text-gray-500 mr-auto">
                  No orders found!
                </div>
              )}
          </div>
          )}
        </div>
      </> : <div className='w-screen flex flex-col items-center justify-center'>
        <SignIn />
      </div>}
      
      <ToastContainer />
    </div>
  );
};